import { createSlice } from '@reduxjs/toolkit'
import { gameClose, userLogin } from 'redux-thunk/thunk/auth.thunk'
import { fetchUserInformation, updateRunItTwicePreferenceThunk } from 'redux-thunk/thunk/user.thunk'
import { getPayloadFromToken } from 'helpers/common.helpers'
import { plus, minus } from 'number-precision'
import { CURRENCY_SYMBOLS } from 'utils/constants/index'

const defaultAuthState = {
  creditWalletWaitQueue: [],
  creditDemoWalletWaitQueue: [],
  userInfo: null,
  userDetails: null,
  currencyId: null,
  currencyCode: null,
  operatorId: null,
  isLoggedIn: false,
  accountInactive: false,
  showName: false,
  demoMode: false,
  demoWallet: { balance: 3000, currency: 'USD' }
}

const authSlice = createSlice({
  name: 'authSlice',
  initialState: defaultAuthState,
  reducers: {
    appendCreditWalletWaitQueue: (state, action) => {
      return {
        ...state,
        creditWalletWaitQueue: [action.payload, ...state.creditWalletWaitQueue]
      }
    },
    updateCreditWalletWaitQueue: (state, action) => {
      let _creditWalletWaitQueue = [...state.creditWalletWaitQueue]
      const _userInfoWallet = _creditWalletWaitQueue[_creditWalletWaitQueue.length - 1] ?? state.userInfo.wallet
      _creditWalletWaitQueue = _creditWalletWaitQueue?.slice(0, _creditWalletWaitQueue.length - 1)
      return {
        ...state,
        creditWalletWaitQueue: _creditWalletWaitQueue,
        userInfo: {
          ...state.userInfo,
          wallet: _userInfoWallet
        }
      }
    },
    updateCreditDemoWalletWaitQueue: (state, action) => {
      let _creditDemoWalletWaitQueue = [...state.creditDemoWalletWaitQueue]
      const _newDemoWallet = _creditDemoWalletWaitQueue.pop() ?? state.demoWallet
      _creditDemoWalletWaitQueue = _creditDemoWalletWaitQueue?.slice(0, _creditDemoWalletWaitQueue.length - 1)
      return {
        ...state,
        creditDemoWalletWaitQueue: _creditDemoWalletWaitQueue,
        demoWallet: _newDemoWallet
      }
    },
    appendCreditDemoWalletWaitQueue: (state, action) => {
      return {
        ...state,
        creditDemoWalletWaitQueue: [action.payload, ...state.creditDemoWalletWaitQueue]
      }
    },
    setShowName: (state, action) => {
      return {
        ...state,
        showName: !state.showName
      }
    },
    setUserInfo: (state, action) => ({
      ...state,
      userInfo: {
        ...state.userInfo,
        wallet: action.payload
      }
    }),
    setDemoMode: (state, action) => {
      return {
        ...state,
        demoMode: action.payload,
        currencyCode: state.demoWallet.currency
      }
    },
    setDemoWalletCurrency: (state, action) => ({
      ...state,
      currencyCode: action.payload.currencyCode,
      currencyId: action.payload?.currencyId,
      currencySymbol: CURRENCY_SYMBOLS?.[action.payload.currencyCode],
      demoWallet: { ...state.demoWallet, currency: action.payload.currencyCode },
      operatorId: action.payload?.operatorId
    }),
    updateDemoWalletBalance: (state, action) => {
      return {
        ...state,
        demoWallet: { ...state.demoWallet, balance: action.payload }
      }
    },
    updateDemoWalletBalanceForBetPlace: (state, action) => {
      const newBalance = (action.payload.operation === 'add') ? state.demoWallet.balance + Number(action.payload.amount) : state.demoWallet.balance - Number(action.payload.amount)

      return {
        ...state,
        demoWallet: { ...state.demoWallet, balance: newBalance }
      }
    },
    debitBalance: (state, action) => {
      if (!state.userInfo?.wallet) return state
      const currentBalance = Number(state.userInfo.wallet.balance ?? 0)
      const _userInfoWallet = {
        ...state.userInfo.wallet,
        balance: minus(currentBalance, +action?.payload)
      }
      return {
        ...state,
        userInfo: {
          ...state.userInfo,
          wallet: _userInfoWallet
        }
      }
    },
    creditBalance: (state, action) => {
      if (!state.userInfo?.wallet) return state
      const currentBalance = Number(state.userInfo.wallet.balance ?? 0)
      return {
        ...state,
        userInfo: {
          ...state.userInfo,
          wallet: {
            ...state.userInfo.wallet,
            balance: plus(currentBalance, +action?.payload)
          }
        }
      }
    },
    updateUserWallet: (state, action) => ({
      ...state,
      userInfo: {
        ...state.userInfo,
        wallet: action.payload
      }
    }),
    sessionExpired: () => defaultAuthState
  },
  extraReducers: (builder) => {
    builder
      .addCase(userLogin.rejected, (state, action) => {
        const message = String(action.payload || '')
        if (/blocked|not active/i.test(message)) {
          state.accountInactive = true
        }
      })
      .addCase(userLogin.fulfilled, (state, action) => {
        const { user, wallet, accessToken } = action.payload
        const payload = getPayloadFromToken(accessToken)
        const currencyId = payload?.currencyId
        const currencyCode = payload?.currencyCode
        const operatorId = payload?.operatorId || '1'
        return {
          ...state,
          currencyId,
          currencyCode,
          operatorId,
          accountInactive: false,
          userInfo: {
            user,
            wallet,
            runItTwicePreference: user?.runItTwicePreference ?? false
          },
          isLoggedIn: true
        }
      })
      .addCase(fetchUserInformation.fulfilled, (state, action) => {
        const profile = action.payload || {}
        const runItTwicePreference = profile.runItTwicePreference
        const { wallet, ...profileWithoutWallet } = profile
        return {
          ...state,
          userDetails: profile,
          userInfo: state.userInfo
            ? {
                ...state.userInfo,
                ...(wallet != null ? { wallet } : {}),
                runItTwicePreference:
                  typeof runItTwicePreference === 'boolean'
                    ? runItTwicePreference
                    : state.userInfo.runItTwicePreference,
                user: Object.keys(profileWithoutWallet).length
                  ? { ...state.userInfo.user, ...profileWithoutWallet }
                  : state.userInfo.user
              }
            : state.userInfo
        }
      })
      .addCase(gameClose.fulfilled, (state, action) => {
        return defaultAuthState
      })
      .addCase(updateRunItTwicePreferenceThunk.fulfilled, (state, action) => {
        const { runItTwicePreference } = action.payload || {}
        if (state.userInfo != null && typeof runItTwicePreference === 'boolean') {
          state.userInfo.runItTwicePreference = runItTwicePreference
        }
      })
  }
})

export const {
  setUserInfo, appendCreditWalletWaitQueue,
  updateCreditWalletWaitQueue, setShowName, appendCreditDemoWalletWaitQueue, updateCreditDemoWalletWaitQueue, debitBalance, creditBalance, updateUserWallet, setDemoMode, updateDemoWalletBalance, updateDemoWalletBalanceForBetPlace, setDemoWalletCurrency, sessionExpired
} = authSlice.actions

export default authSlice.reducer
